import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import API_BASE from "../../config";
import s from "./ReportsPage.module.css";

const WINDOWS = [
  { value: "12mo",       label: "Rolling last 12 months" },
  { value: "this-year",  label: "This calendar year" },
  { value: "last-year",  label: "Last calendar year" },
  { value: "ytd",        label: "Year to date" },
  { value: "custom",     label: "Custom range" },
];

const STATUS_FILTERS = [
  { value: "all",      label: "All statuses" },
  { value: "active",   label: "Active only" },
  { value: "inactive", label: "Inactive / separated" },
];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Resolve a window key to {from, to} ISO date strings. Custom returns the
 *  caller-supplied dates verbatim. */
function resolveWindow(key, customFrom, customTo) {
  const today = new Date();
  if (key === "12mo") {
    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 1);
    start.setDate(start.getDate() + 1);
    return { from: ymd(start), to: ymd(today) };
  }
  if (key === "this-year") {
    return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
  }
  if (key === "last-year") {
    const y = today.getFullYear() - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  if (key === "ytd") {
    return { from: `${today.getFullYear()}-01-01`, to: ymd(today) };
  }
  return { from: customFrom, to: customTo };
}

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "PHP", maximumFractionDigits: 2 });
}

function num(n, digits = 1) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Tolerant JSON parser — leavesJson may be null, an empty string, an object,
 *  or a stringified array. Returns [] for anything we can't read. */
function safeParse(raw) {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

/** Sum the `days` field of a leavesJson array. Robust to string / number /
 *  missing fields. */
function totalLeaveDays(leavesJson) {
  const list = safeParse(leavesJson);
  if (!Array.isArray(list)) return 0;
  return list.reduce((sum, l) => sum + (Number(l?.days) || 0), 0);
}

/** Parse Employee.deductionsJson into a flat list of {name, value} rows +
 *  total. The blob shape is
 *    { sss, philhealth, pagibig, insurance, others: [{name, value}, ...] }
 *  Statutory keys map to fixed labels; "others" passes through as-is.
 *  Zero / missing values are kept so the modal can show a row even when
 *  the value is zero (admins want to see "SSS — 0.00" rather than have
 *  the row vanish silently). */
function parseDeductions(deductionsJson) {
  const obj = safeParse(deductionsJson);
  const rows = [];
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    rows.push({ name: "SSS",         value: Number(obj.sss        || 0) });
    rows.push({ name: "PhilHealth",  value: Number(obj.philhealth || 0) });
    rows.push({ name: "Pag-IBIG",    value: Number(obj.pagibig    || 0) });
    rows.push({ name: "Insurance",   value: Number(obj.insurance  || 0) });
    if (Array.isArray(obj.others)) {
      for (const o of obj.others) {
        rows.push({ name: String(o?.name || "(unnamed)"), value: Number(o?.value || 0) });
      }
    }
  }
  const total = rows.reduce((s, r) => s + r.value, 0);
  return { rows, total };
}

/** Parse Employee.earningsJson — already a flat array of {name, value}. */
function parseEarnings(earningsJson) {
  const list = safeParse(earningsJson);
  const rows = Array.isArray(list)
    ? list.map(e => ({ name: String(e?.name || "(unnamed)"), value: Number(e?.value || 0) }))
    : [];
  const total = rows.reduce((s, r) => s + r.value, 0);
  return { rows, total };
}

/** Encode a value for CSV output. Wraps in quotes when the value contains a
 *  comma, quote, or newline; doubles up internal quotes per RFC 4180. */
function csvCell(v) {
  if (v == null) return "";
  const str = String(v);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

/** Trigger a browser download for a generated CSV string. */
function downloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const COLUMNS = [
  { key: "name",            label: "Employee",       align: "left",  type: "text" },
  { key: "code",            label: "Code",           align: "left",  type: "text" },
  { key: "role",            label: "Role",           align: "left",  type: "text" },
  { key: "department",      label: "Department",     align: "left",  type: "text" },
  { key: "status",          label: "Status",         align: "left",  type: "text" },
  { key: "salaryType",      label: "Salary type",    align: "left",  type: "text" },
  { key: "hireDate",        label: "Joined",         align: "left",  type: "date" },
  { key: "salary",          label: "Monthly",        align: "right", type: "money" },
  { key: "annualized",      label: "Annualized",     align: "right", type: "money" },
  { key: "monthsInWindow",  label: "Months (win)",   align: "right", type: "num1" },
  { key: "windowEarnings",  label: "Earnings (win)", align: "right", type: "money" },
  // Configured deductions / earnings come straight off the employee
  // record, so they're independent of the chosen window.
  { key: "deductionsTotal", label: "Deductions",     align: "right", type: "moneyAction" },
  { key: "earningsTotal",   label: "Earnings",       align: "right", type: "moneyAction" },
  { key: "daysWorked",      label: "Days worked",    align: "right", type: "num0" },
  { key: "totalHours",      label: "Hours",          align: "right", type: "num1" },
  { key: "otHours",          label: "OT hrs",        align: "right", type: "num1" },
  { key: "lateMin",         label: "Late (min)",     align: "right", type: "num0" },
  { key: "underMin",        label: "Under (min)",    align: "right", type: "num0" },
  { key: "leaveDays",       label: "Leaves (d)",     align: "right", type: "num0" },
];

/** Days between two yyyy-mm-dd strings, inclusive end. Returns 0 when either
 *  side is missing or end is before start. */
function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const a = new Date(fromIso);
  const b = new Date(toIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.floor((b - a) / 86400000) + 1;
}

/** Months that overlap between [hireDate, today] and [windowFrom, windowTo].
 *  Approximated as days / 30.4375. Capped at 12 so a long-tenured employee
 *  in a 12-month window doesn't show 13. Returns null when hireDate is
 *  unknown — UI renders that as "—" rather than guessing. */
function monthsActiveInWindow(hireIso, windowFrom, windowTo) {
  if (!hireIso) return null;
  const today = new Date();
  const todayIso = ymd(today);
  const overlapStart = hireIso > windowFrom ? hireIso : windowFrom;
  const overlapEnd   = todayIso < windowTo  ? todayIso : windowTo;
  const days = daysBetween(overlapStart, overlapEnd);
  return Math.min(12, days / 30.4375);
}

/** Comprehensive payroll report — one row per employee for a chosen window.
 *  Combines the static employee record (salary, role, leaves config) with
 *  timesheet aggregates pulled from the new /api/timesheets/payroll-summary
 *  endpoint. Sortable, filterable, exportable to CSV. */
export default function PayrollReport({ employees, monthlyPayroll, payrollRows, tuitionIn, tuitionRangeLabel }) {
  const [windowKey,  setWindowKey]  = useState("12mo");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [department,   setDepartment]   = useState("all");
  const [search,       setSearch]       = useState("");
  const [sortKey,      setSortKey]      = useState("salary");
  const [sortDir,      setSortDir]      = useState("desc");
  const [aggregates,   setAggregates]   = useState({});
  const [aggLoading,   setAggLoading]   = useState(false);
  const [aggErr,       setAggErr]       = useState("");
  // Breakdown modal — set to { row, kind: "deductions" | "earnings" }
  // when an admin clicks the "i" icon on a Deductions / Earnings cell.
  const [breakdown,    setBreakdown]    = useState(null);
  const [exportOpen,   setExportOpen]   = useState(false);
  const exportMenuRef  = useRef(null);

  // Click-away close for the export format menu.
  useEffect(() => {
    if (!exportOpen) return undefined;
    function onDocClick(e) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [exportOpen]);

  const { from, to } = useMemo(
    () => resolveWindow(windowKey, customFrom, customTo),
    [windowKey, customFrom, customTo]
  );

  // Pull timesheet aggregates from the backend whenever the window changes.
  // Custom range only fires once both dates are filled in.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setAggLoading(true);
    setAggErr("");
    fetch(`${API_BASE}/api/timesheets/payroll-summary?from=${from}&to=${to}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(data => { if (!cancelled) setAggregates(data || {}); })
      .catch(e => { if (!cancelled) { setAggregates({}); setAggErr(e.message); } })
      .finally(() => { if (!cancelled) setAggLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  // Distinct department list, derived from the employees we already have.
  // Sorted alphabetically; empty/null departments skipped.
  const departments = useMemo(() => {
    const set = new Set();
    for (const e of employees) {
      if (e.department) set.add(e.department);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [employees]);

  // Build the joined dataset: each employee + their aggregate row.
  const rows = useMemo(() => employees.map(e => {
    const agg = aggregates[e.code] || {};
    const monthly = Number(e.salary || 0);
    const hireDate = e.hireDate || "";
    const months = monthsActiveInWindow(hireDate, from, to);
    // Pro-rated earnings for the chosen window. If hireDate is missing we
    // fall back to a flat 12-month annualized so the column is still
    // sortable; the cell renders the value but the absence of "Joined" on
    // the same row is the clue that pro-ration didn't apply.
    const windowEarnings = months == null ? monthly * 12 : monthly * months;
    const dedu = parseDeductions(e.deductionsJson);
    const earn = parseEarnings(e.earningsJson);
    return {
      id:             e.id,
      code:           e.code || "",
      name:           e.name || "",
      role:           e.role || "",
      department:     e.department || "",
      status:         e.status || "Active",
      salaryType:     e.salaryType || "monthly",
      hireDate,
      salary:         monthly,
      annualized:     monthly * 12,
      monthsInWindow: months,
      windowEarnings,
      deductionsTotal: dedu.total,
      deductionsRows:  dedu.rows,
      earningsTotal:   earn.total,
      earningsRows:    earn.rows,
      daysWorked:     Number(agg.daysWorked || 0),
      totalHours:     Number(agg.totalHours || 0),
      otHours:        Number(agg.overtimeHoursApproved || 0),
      lateMin:        Number(agg.totalLateMinutes || 0),
      underMin:       Number(agg.totalUndertimeMinutes || 0),
      leaveDays:      totalLeaveDays(e.leavesJson),
    };
  }), [employees, aggregates, from, to]);

  // Apply UI filters.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const isActive = (r.status || "Active") !== "Inactive";
      if (statusFilter === "active"   && !isActive) return false;
      if (statusFilter === "inactive" &&  isActive) return false;
      if (department !== "all" && r.department !== department) return false;
      if (q) {
        const hay = `${r.name} ${r.code} ${r.role} ${r.department}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, department, search]);

  // Sort. Money / number columns use numeric compare; text columns use a
  // locale-aware string compare; date columns sort lexicographically (yyyy-
  // mm-dd already orders correctly that way). Direction comes from the
  // column header toggle.
  const sorted = useMemo(() => {
    const col = COLUMNS.find(c => c.key === sortKey);
    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (col?.type === "text" || col?.type === "date") {
        return String(va || "").localeCompare(String(vb || "")) * dir;
      }
      return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      // First click on a numeric column defaults to descending (largest
      // first); text columns default to ascending (A-Z).
      const col = COLUMNS.find(c => c.key === key);
      setSortDir(col?.type === "text" ? "asc" : "desc");
    }
  }

  function exportCsv() {
    const header = ["Window", `${from} to ${to}`];
    const colHeaders = COLUMNS.map(c => c.label);
    const dataRows = sorted.map(r => COLUMNS.map(c => {
      const v = r[c.key];
      if (c.type === "money" || c.type === "moneyAction") return Number(v || 0).toFixed(2);
      if (c.type === "num0")  return String(Math.round(Number(v || 0)));
      if (c.type === "num1")  return v == null ? "" : Number(v).toFixed(1);
      if (c.type === "date")  return v || "";
      return v ?? "";
    }));
    // Build the totals row positionally so a column reorder above stays in
    // sync with the right-hand numeric sums.
    const totalRow = COLUMNS.map(c => {
      if (c.key === "name") return "TOTAL";
      if (c.type === "money" || c.type === "moneyAction") {
        return sorted.reduce((s, r) => s + Number(r[c.key] || 0), 0).toFixed(2);
      }
      if (c.type === "num1")  return sorted.reduce((s, r) => s + Number(r[c.key] || 0), 0).toFixed(1);
      if (c.type === "num0")  return String(sorted.reduce((s, r) => s + Number(r[c.key] || 0), 0));
      return "";
    });
    downloadCsv(`payroll-report_${from}_to_${to}.csv`,
      [header, [], colHeaders, ...dataRows, [], totalRow]);
  }

  /** PDF export — landscape A4, autoTable handles pagination so a large
   *  staff list flows over multiple pages. The same column ordering and
   *  numeric formatting as CSV so a printed copy reconciles to a saved
   *  spreadsheet. */
  function exportPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Staff Payroll Report", 14, 14);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(`Window: ${from} to ${to}    ·    ${sorted.length} staff`, 14, 20);
    doc.setTextColor(0);

    const head = [COLUMNS.map(c => c.label)];
    const body = sorted.map(r => COLUMNS.map(c => {
      const v = r[c.key];
      if (c.type === "money" || c.type === "moneyAction") {
        return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (c.type === "num0") return String(Math.round(Number(v || 0)));
      if (c.type === "num1") return v == null ? "—" : Number(v).toFixed(1);
      if (c.type === "date") return v || "—";
      return String(v ?? "");
    }));
    // Map column key → totals object key. Most match by name, except the
    // Monthly column ("salary") which sums into totals.monthly.
    const totalKeyMap = {
      salary: "monthly", annualized: "annualized", windowEarnings: "windowEarnings",
      deductionsTotal: "deductionsTotal", earningsTotal: "earningsTotal",
      daysWorked: "daysWorked", totalHours: "totalHours", otHours: "otHours",
      lateMin: "lateMin", underMin: "underMin", leaveDays: "leaveDays",
    };
    const foot = [COLUMNS.map(c => {
      if (c.key === "name") return `TOTAL · ${sorted.length} staff`;
      if (c.key === "monthsInWindow") {
        return totals.avgMonths != null ? `${totals.avgMonths.toFixed(1)} avg` : "—";
      }
      const tk = totalKeyMap[c.key];
      if (tk == null) return "";
      const v = totals[tk];
      if (c.type === "money" || c.type === "moneyAction") {
        return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (c.type === "num1") return Number(v || 0).toFixed(1);
      if (c.type === "num0") return String(Math.round(Number(v || 0)));
      return String(v ?? "");
    })];
    autoTable(doc, {
      startY: 24,
      head, body, foot,
      styles:      { fontSize: 6.5, cellPadding: 1.4, overflow: "linebreak" },
      headStyles:  { fillColor: [250, 242, 221], textColor: [138, 109, 40], fontStyle: "bold" },
      footStyles:  { fillColor: [252, 250, 244], textColor: [26, 26, 26], fontStyle: "bold" },
      bodyStyles:  { textColor: [26, 26, 26] },
      alternateRowStyles: { fillColor: [253, 251, 244] },
      columnStyles: COLUMNS.reduce((acc, c, i) => {
        acc[i] = { halign: c.align === "right" ? "right" : "left" };
        return acc;
      }, {}),
      margin: { left: 8, right: 8 },
      didDrawPage: (data) => {
        const page  = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8); doc.setTextColor(140);
        doc.text(`Page ${data.pageNumber} of ${page}`, pageW - 14, doc.internal.pageSize.getHeight() - 6, { align: "right" });
        doc.setTextColor(0);
      },
    });

    doc.save(`payroll-report_${from}_to_${to}.pdf`);
  }

  function formatCell(r, col) {
    const v = r[col.key];
    if (col.type === "money") return money(v);
    if (col.type === "num1")  return v == null ? "—" : num(v, 1);
    if (col.type === "num0")  return num(v, 0);
    if (col.type === "date")  return v || "—";
    if (col.type === "moneyAction") {
      const kind = col.key === "deductionsTotal" ? "deductions" : "earnings";
      const list = kind === "deductions" ? r.deductionsRows : r.earningsRows;
      const hasAny = Array.isArray(list) && list.length > 0;
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
          <span>{money(v)}</span>
          <button
            type="button"
            className={s.infoBtn}
            title={hasAny ? `Show ${kind} breakdown` : `No ${kind} configured`}
            aria-label={`Show ${kind} breakdown`}
            disabled={!hasAny}
            onClick={(e) => { e.stopPropagation(); if (hasAny) setBreakdown({ row: r, kind }); }}
            style={hasAny ? null : { opacity: 0.35, cursor: "not-allowed" }}>
            i
          </button>
        </span>
      );
    }
    return v;
  }

  // Footer totals for whatever the user is currently viewing (post-filter).
  // Only numeric columns are summed; date / text / months-in-window stay
  // blank.
  const totals = useMemo(() => {
    // Average months-in-window across staff with a known hire date (rows
    // where monthsInWindow is null are skipped so the average isn't
    // skewed by unknowns).
    const monthsKnown = sorted.filter(r => r.monthsInWindow != null);
    const avgMonths = monthsKnown.length
      ? monthsKnown.reduce((s, r) => s + r.monthsInWindow, 0) / monthsKnown.length
      : null;
    return {
      monthly:         sorted.reduce((s, r) => s + r.salary,          0),
      annualized:      sorted.reduce((s, r) => s + r.annualized,      0),
      windowEarnings:  sorted.reduce((s, r) => s + r.windowEarnings,  0),
      deductionsTotal: sorted.reduce((s, r) => s + r.deductionsTotal, 0),
      earningsTotal:   sorted.reduce((s, r) => s + r.earningsTotal,   0),
      daysWorked:      sorted.reduce((s, r) => s + r.daysWorked,      0),
      totalHours:      sorted.reduce((s, r) => s + r.totalHours,      0),
      otHours:         sorted.reduce((s, r) => s + r.otHours,         0),
      lateMin:         sorted.reduce((s, r) => s + r.lateMin,         0),
      underMin:        sorted.reduce((s, r) => s + r.underMin,        0),
      leaveDays:       sorted.reduce((s, r) => s + r.leaveDays,       0),
      avgMonths,
      monthsKnownCount: monthsKnown.length,
    };
  }, [sorted]);

  /** Aggregate every staff record's {name, value} rows into one list,
   *  preserving the order of first appearance. Used by the footer-level
   *  Deductions / Earnings breakdown modal so admins see "Total SSS to
   *  remit" rather than just the sum of all categories. */
  function aggregateBreakdown(rowsKey) {
    const order = [];
    const byName = new Map();
    for (const r of sorted) {
      const list = r[rowsKey] || [];
      for (const item of list) {
        const k = item.name;
        if (!byName.has(k)) {
          order.push(k);
          byName.set(k, 0);
        }
        byName.set(k, byName.get(k) + Number(item.value || 0));
      }
    }
    return order.map(name => ({ name, value: byName.get(name) }));
  }

  return (
    <>
      {/* Headline tiles. Configured payroll comes from the parent (already
          computed there). Tuition reference keeps the in-vs-out comparison
          visible on this tab. */}
      <div className={s.moneyGrid}>
        <MoneyTile kind="out" label="Monthly payroll (configured)" value={monthlyPayroll}
                   sub={`${payrollRows.length} active staff with salary`}
                   emphasis />
        <MoneyTile kind="out" label="Annualized payroll" value={monthlyPayroll * 12}
                   sub="monthly × 12 reference" />
        <MoneyTile kind="out" label="Average per staff"
                   value={payrollRows.length ? monthlyPayroll / payrollRows.length : 0}
                   sub="mean of configured salaries" />
        <MoneyTile kind="in" label={`Tuition received (${tuitionRangeLabel})`}
                   value={tuitionIn}
                   sub="reference · see Tuition & Fees tab" />
      </div>

      {/* Filter row — window picker + status + department + search + export */}
      <div className={s.filterRow}>
        <label className={s.filterField}>
          <span>Window</span>
          <select value={windowKey} onChange={e => setWindowKey(e.target.value)}>
            {WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </label>

        {windowKey === "custom" && (
          <>
            <label className={s.filterField}>
              <span>From</span>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            </label>
            <label className={s.filterField}>
              <span>To</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </label>
          </>
        )}

        <label className={s.filterField}>
          <span>Status</span>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>

        <label className={s.filterField}>
          <span>Department</span>
          <select value={department} onChange={e => setDepartment(e.target.value)}>
            <option value="all">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>

        <label className={`${s.filterField} ${s.filterGrow}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <input
              type="text"
              placeholder="Name, code, role, department"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" className={s.clearBtn}
                      onClick={() => setSearch("")}
                      aria-label="Clear search">×</button>
            )}
          </div>
        </label>

        <div className={s.exportMenuWrap} ref={exportMenuRef}>
          <button type="button"
                  className={s.exportBtn}
                  onClick={() => setExportOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={exportOpen}>
            Export ▾
          </button>
          {exportOpen && (
            <div className={s.exportMenu} role="menu">
              <button type="button"
                      className={s.exportMenuItem}
                      role="menuitem"
                      onClick={() => { setExportOpen(false); exportCsv(); }}>
                Export as CSV
                <span className={s.exportMenuHint}>spreadsheet · all columns</span>
              </button>
              <button type="button"
                      className={s.exportMenuItem}
                      role="menuitem"
                      onClick={() => { setExportOpen(false); exportPdf(); }}>
                Export as PDF
                <span className={s.exportMenuHint}>printable · landscape A4</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <section className={s.listCard}>
        <header className={s.listHead}>
          <h3>Staff payroll · {from} to {to}</h3>
          <span className={s.listCount}>
            {sorted.length} of {employees.length} staff
            {aggLoading ? " · loading timesheets…" : ""}
          </span>
        </header>
        <div className={s.listCardHint}>
          Reference report — monthly salary is the configured value on each
          staff record; days, hours, OT, late and undertime are pulled from
          the timesheet log for the selected window. Once a payroll ledger
          exists, actual disbursements will join this view.
        </div>
        {aggErr && <div className={s.error}>Could not load timesheet aggregates: {aggErr}</div>}

        <div className={s.payrollTableWrap}>
          <table className={`${s.payrollTable} ${s.payrollTableWide}`}>
            <thead>
              <tr>
                {COLUMNS.map(c => {
                  const active = sortKey === c.key;
                  const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                  return (
                    <th key={c.key}
                        className={`${c.align === "right" ? s.colRight : ""} ${active ? s.colActive : ""}`}
                        onClick={() => toggleSort(c.key)}
                        title="Sort by this column">
                      {c.label}{arrow}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={s.listEmpty}>
                    No employees match the current filters.
                  </td>
                </tr>
              ) : sorted.map(r => (
                <tr key={r.id}>
                  {COLUMNS.map(c => (
                    <td key={c.key} className={c.align === "right" ? s.colRight : ""}>
                      {formatCell(r, c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {sorted.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={7} className={s.colFooterLabel}>TOTAL · {sorted.length} staff</td>
                  <td className={s.colRight}>{money(totals.monthly)}</td>
                  <td className={s.colRight}>{money(totals.annualized)}</td>
                  <td className={s.colRight}
                      title={totals.avgMonths != null
                        ? `Average across ${totals.monthsKnownCount} of ${sorted.length} staff with a hire date`
                        : "No staff have a hire date set"}>
                    {totals.avgMonths != null ? `${num(totals.avgMonths, 1)} avg` : "—"}
                  </td>
                  <td className={s.colRight}>{money(totals.windowEarnings)}</td>
                  <td className={s.colRight}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                      <span>{money(totals.deductionsTotal)}</span>
                      <button type="button" className={s.infoBtn}
                              title="Aggregate deductions breakdown"
                              aria-label="Aggregate deductions breakdown"
                              disabled={totals.deductionsTotal === 0}
                              style={totals.deductionsTotal === 0 ? { opacity: 0.35, cursor: "not-allowed" } : null}
                              onClick={() => totals.deductionsTotal !== 0 && setBreakdown({
                                kind: "deductions",
                                aggregate: { rows: aggregateBreakdown("deductionsRows"), total: totals.deductionsTotal, label: `${sorted.length} staff` },
                              })}>i</button>
                    </span>
                  </td>
                  <td className={s.colRight}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                      <span>{money(totals.earningsTotal)}</span>
                      <button type="button" className={s.infoBtn}
                              title="Aggregate earnings breakdown"
                              aria-label="Aggregate earnings breakdown"
                              disabled={totals.earningsTotal === 0}
                              style={totals.earningsTotal === 0 ? { opacity: 0.35, cursor: "not-allowed" } : null}
                              onClick={() => totals.earningsTotal !== 0 && setBreakdown({
                                kind: "earnings",
                                aggregate: { rows: aggregateBreakdown("earningsRows"), total: totals.earningsTotal, label: `${sorted.length} staff` },
                              })}>i</button>
                    </span>
                  </td>
                  <td className={s.colRight}>{num(totals.daysWorked, 0)}</td>
                  <td className={s.colRight}>{num(totals.totalHours, 1)}</td>
                  <td className={s.colRight}>{num(totals.otHours,    1)}</td>
                  <td className={s.colRight}>{num(totals.lateMin,   0)}</td>
                  <td className={s.colRight}>{num(totals.underMin,  0)}</td>
                  <td className={s.colRight}>{num(totals.leaveDays, 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {breakdown && (
        <BreakdownModal
          row={breakdown.row}
          kind={breakdown.kind}
          aggregate={breakdown.aggregate}
          onClose={() => setBreakdown(null)}
        />
      )}
    </>
  );
}

/** Deductions / earnings breakdown — works for both per-row and aggregate
 *  views. When `aggregate` is provided the title becomes "All deductions
 *  (N staff)" and rows are pre-summed; otherwise it falls back to the
 *  per-employee `row.deductionsRows / earningsRows`. */
function BreakdownModal({ row, kind, aggregate, onClose }) {
  const isDeductions = kind === "deductions";
  const list  = aggregate ? aggregate.rows  : (isDeductions ? row.deductionsRows  : row.earningsRows);
  const total = aggregate ? aggregate.total : (isDeductions ? row.deductionsTotal : row.earningsTotal);
  const headLabel = isDeductions ? "deductions" : "earnings";
  const title = aggregate
    ? `All ${headLabel} · ${aggregate.label}`
    : `${row.name} — Configured ${headLabel}`;
  const emptyMsg = aggregate
    ? `No ${headLabel} configured across the visible staff.`
    : `No ${headLabel} configured on this staff record.`;

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>{title}</h3>
          <button type="button" className={s.modalClose}
                  onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.detailsGrid}>
          {list.length === 0 ? (
            <div className={s.listEmpty}>{emptyMsg}</div>
          ) : list.map((item, i) => (
            <div key={`${item.name}-${i}`} className={s.detailsRow}>
              <span className={s.detailsLabel}>{item.name}</span>
              <span className={s.detailsValue}>{money(item.value)}</span>
            </div>
          ))}
          {list.length > 0 && (
            <div className={s.detailsRow} style={{ borderTop: "2px solid #ece2c9", marginTop: 4, paddingTop: 10 }}>
              <span className={s.detailsLabel} style={{ fontWeight: 800, color: "#1a1a1a" }}>Total</span>
              <span className={s.detailsValue} style={{
                fontWeight: 800,
                color: isDeductions ? "#b06628" : "#4a6d55",
              }}>
                {money(total)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function MoneyTile({ kind, label, value, sub, emphasis }) {
  const display = value == null ? "—" : money(value);
  return (
    <div className={`${s.moneyTile} ${s[`moneyTile_${kind}`]} ${emphasis ? s.moneyTileEmph : ""}`}>
      <div className={s.moneyLabel}>{label}</div>
      <div className={s.moneyValue}>{display}</div>
      {sub && <div className={s.moneySub}>{sub}</div>}
    </div>
  );
}
